import { formatBytes, TRASH_RETENTION_DAYS, type TrashItem } from "@cva/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileImage, FolderKanban, Image, RotateCcw, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, EmptyState, PageHeader, Skeleton } from "../components/ui/card";
import { ConfirmDialog } from "../components/ui/confirm";
import { ContextMenuPopup, useContextMenu } from "../components/ui/context-menu";
import { DropdownItem, DropdownSeparator } from "../components/ui/dropdown";
import { orpc } from "../lib/api";
import { useOrgId, useOrgRole } from "../lib/org";
import { errorMessage, formatDate, formatRelative } from "../lib/utils";

const TYPE_LABELS: Record<TrashItem["type"], string> = {
  project: "Projekt",
  document: "Datei",
  asset: "Medium",
};
const TYPE_ICONS = { project: FolderKanban, document: FileImage, asset: Image };

export function TrashPage() {
  const orgId = useOrgId();
  const queryClient = useQueryClient();
  const { can } = useOrgRole(orgId);
  const [purging, setPurging] = useState<TrashItem | null>(null);
  const menu = useContextMenu<TrashItem>();
  const trash = useQuery(orpc.trash.list.queryOptions({ input: { orgId } }));
  const invalidate = () => queryClient.invalidateQueries();
  const restore = useMutation(
    orpc.trash.restore.mutationOptions({
      onSuccess: async () => {
        await invalidate();
        toast.success("Wiederhergestellt");
      },
      onError: (error) => toast.error(errorMessage(error)),
    }),
  );
  const purge = useMutation(
    orpc.trash.purge.mutationOptions({ onError: (error) => toast.error(errorMessage(error)) }),
  );
  const total = trash.data?.reduce((sum, item) => sum + item.sizeBytes, 0) ?? 0;

  return (
    <>
      <PageHeader
        title="Papierkorb"
        description={`Gelöschte Einträge werden nach ${TRASH_RETENTION_DAYS} Tagen endgültig entfernt. Bis dahin belegen sie weiter Speicher${total ? ` (aktuell ${formatBytes(total)})` : ""}.`}
      />
      {trash.isLoading ? (
        <Skeleton className="h-48" />
      ) : trash.data?.length === 0 ? (
        <EmptyState icon={<Trash2 />} title="Der Papierkorb ist leer" />
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead className="border-b border-zinc-100 bg-zinc-50 text-left text-xs text-zinc-500">
              <tr>
                <th className="px-5 py-2.5 font-medium">Name</th>
                <th className="px-5 py-2.5 font-medium">Gelöscht</th>
                <th className="px-5 py-2.5 font-medium">Endgültig weg am</th>
                <th className="px-5 py-2.5 text-right font-medium">Größe</th>
                <th className="px-5 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {trash.data?.map((item) => {
                const Icon = TYPE_ICONS[item.type];
                return (
                  <tr
                    key={`${item.type}-${item.id}`}
                    onContextMenu={
                      can("trash.restore") || can("trash.purge") ? menu.open(item) : undefined
                    }
                    className="hover:bg-zinc-50/60"
                  >
                    <td className="px-5 py-3">
                      <span className="flex items-center gap-2.5">
                        <Icon className="size-4 text-zinc-400" />
                        <span className="font-medium">{item.name}</span>
                        <span className="text-xs text-zinc-400">{TYPE_LABELS[item.type]}</span>
                      </span>
                    </td>
                    <td className="px-5 py-3 text-zinc-600">{formatRelative(item.deletedAt)}</td>
                    <td className="px-5 py-3 text-zinc-600">{formatDate(item.purgeAt)}</td>
                    <td className="px-5 py-3 text-right text-zinc-600">
                      {formatBytes(item.sizeBytes)}
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex justify-end gap-1">
                        {can("trash.restore") ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => restore.mutate({ orgId, type: item.type, id: item.id })}
                          >
                            <RotateCcw /> Wiederherstellen
                          </Button>
                        ) : null}
                        {can("trash.purge") ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-600 hover:bg-red-50"
                            onClick={() => setPurging(item)}
                          >
                            Endgültig löschen
                          </Button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}
      <ContextMenuPopup at={menu.at} onClose={menu.close}>
        {menu.target ? (
          <>
            {can("trash.restore") ? (
              <DropdownItem
                onSelect={() =>
                  menu.target &&
                  restore.mutate({ orgId, type: menu.target.type, id: menu.target.id })
                }
              >
                <RotateCcw /> Wiederherstellen
              </DropdownItem>
            ) : null}
            {can("trash.purge") ? (
              <>
                {can("trash.restore") ? <DropdownSeparator /> : null}
                <DropdownItem danger onSelect={() => setPurging(menu.target)}>
                  <Trash2 /> Endgültig löschen …
                </DropdownItem>
              </>
            ) : null}
          </>
        ) : null}
      </ContextMenuPopup>
      <ConfirmDialog
        open={purging !== null}
        onOpenChange={(open) => !open && setPurging(null)}
        danger
        title={`„${purging?.name}“ endgültig löschen?`}
        description="Das lässt sich nicht rückgängig machen. Alle zugehörigen Dateien werden vom Server entfernt."
        confirmLabel="Endgültig löschen"
        onConfirm={async () => {
          if (!purging) return;
          const result = await purge.mutateAsync({ orgId, type: purging.type, id: purging.id });
          await invalidate();
          toast.success(`${formatBytes(result.freedBytes)} freigegeben`);
        }}
      />
    </>
  );
}
